import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import { StepIconProps } from '@mui/material/StepIcon'
import Typography from '@mui/material/Typography'

import PageContainer from '@/components/ui/containers/PageContainer'

import ConfigureCpsCardStep from './containers/ConfigureCpsCardStep'
import ConfigureStripeStep from './containers/ConfigureStripeStep'
import ConfirmEmailStep from './containers/ConfirmEmailStep'
import ConfirmPhoneNumberStep from './containers/ConfirmPhoneNumberStep'

// import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
// import { selectAuthUser, updateMyProfile } from '@/redux/reducers/auth.reducer'

function StepIcon(props: StepIconProps) {
  const { active, completed, sx = [] } = props
  return (
    <Box
      sx={[
        {
          width: '100%',
          height: '4px',
          backgroundColor: (theme) => (active || completed ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.25)'),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  )
}

export function GettingStartedPage() {
  // const dispatch = useAppDispatch()
  // const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // const authUser = useAppSelector(selectAuthUser)

  const completeAllStep = () => {
    // dispatch(updateMyProfile({ isFirstConnection: false }))
    navigate('/configuration')
  }

  const incrementStep = () => {
    const nextStep = currentStep + 1

    if (nextStep >= steps.length) {
      completeAllStep()
      return
    }

    if (searchParams.toString() !== '') setSearchParams('')
    setCurrentStep(nextStep)
  }

  const decrementStep = () => {
    const previousStep = currentStep - 1

    if (previousStep < 0) return

    if (searchParams.toString() !== '') setSearchParams('')
    setCurrentStep(previousStep)
  }

  const handleSkipStep = () => {
    incrementStep()
  }

  const handleSkipAll = () => {
    completeAllStep()
  }

  const steps = [
    {
      id: 'confirm-email',
      title: t('page.gettingStarted.steps.confirmEmailStep.title'),
      description: t('page.gettingStarted.steps.confirmEmailStep.description'),
      component: (
        <ConfirmEmailStep
          onComplete={() => {
            incrementStep()
          }}
        />
      ),
    },
    {
      id: 'confirm-phone-number',
      title: t('page.gettingStarted.steps.confirmPhoneNumberStep.title'),
      description: t('page.gettingStarted.steps.confirmPhoneNumberStep.description'),
      component: (
        <ConfirmPhoneNumberStep
          onComplete={() => {
            incrementStep()
          }}
        />
      ),
    },
    {
      id: 'configure-stripe',
      title: t('page.gettingStarted.steps.configureStripeStep.title'),
      description: t('page.gettingStarted.steps.configureStripeStep.description'),
      component: <ConfigureStripeStep />,
    },
    {
      id: 'configure-cps-card',
      title: t('page.gettingStarted.steps.configureCpsCardStep.title'),
      description: t('page.gettingStarted.steps.configureCpsCardStep.description'),
      component: (
        <ConfigureCpsCardStep
          onComplete={() => {
            completeAllStep()
          }}
        />
      ),
    },
  ]

  const [currentStep, setCurrentStep] = useState<number>(() => {
    const stepId = searchParams.get('step')
    return stepId ? steps.findIndex((step) => step.id === stepId) : 0
  })

  // if (authUser && !authUser.isFirstConnection) {
  //   return <Navigate to={_.get(location, 'state.from', '/')} />
  // }

  return (
    <Box sx={{ backgroundColor: (theme) => theme.palette.background.default }}>
      <PageContainer maxWidth="sm">
        <Stack spacing={2} sx={{ pt: '96px', pb: '96px' }}>
          <Stack spacing={2} sx={{ pl: '20px', pr: '20px' }}>
            <Box sx={{ pl: '5px', pr: '5px' }}>
              <Typography variant="h1" sx={{ fontSize: '30px', fontWeight: 700 }}>
                {steps[currentStep]?.title}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontSize: '14px', fontWeigt: 400 }}>
                {steps[currentStep]?.description}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ pl: '5px' }}>
                {t('page.gettingStarted.stepper.label', { currentStep: currentStep + 1, totalStep: steps.length })}
              </Typography>
              <Stepper activeStep={currentStep} connector={null}>
                {steps.map((step) => (
                  <Step key={step.id} sx={{ flex: 1, p: '0px' }}>
                    <StepLabel
                      StepIconComponent={StepIcon}
                      sx={{
                        flex: 1,
                        '& .MuiStepLabel-iconContainer': {
                          width: '100%',
                          pl: '5px',
                          pr: '5px',
                        },
                        '& .MuiStepLabel-labelContainer': {
                          display: 'none',
                        },
                      }}
                    />
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Stack>
          <Paper sx={{ p: '40px' }}>{steps[currentStep].component}</Paper>
          <Stack direction="row" justifyContent={currentStep === 0 ? 'flex-end' : 'space-between'}>
            {currentStep !== 0 && <Button onClick={decrementStep}>{t('common:button.previous.label')}</Button>}
            <Button onClick={handleSkipStep}>{t('common:button.skip.label')}</Button>
            {currentStep < steps.length - 1 && <Button onClick={handleSkipAll}>{t('common:button.skipAll.label')}</Button>}
          </Stack>
        </Stack>
      </PageContainer>
    </Box>
  )
}

export default GettingStartedPage
